/**
 * @file routes/_app/non-conformities/list.tsx
 * @description 「稽核員專區 > 問題與報告 > 問題追蹤」Tab 容器頁面。
 *
 * 4 個 Tab：
 *  1. NC 報告（本檔內聯 NCReportsContent）— 讀 audit_findings 不符合事項（重大 / 一般）
 *  2. 觀察事項（dynamic import ObservationsTab）— 讀 audit_findings 觀察事項 / 改善建議
 *  3. 追蹤稽核（dynamic import FollowUpTab）— 讀 audit_findings 需改正發現的改善追蹤生命週期
 *  4. 改善追蹤（dynamic import ImprovementsTab）
 *
 * ⚠️ 資料來源已由 MOCK 假資料改為 `GET /api/nc-reports`（現場實地稽核已填的真實發現事項）。
 *   後端對稽核員做行級過濾（只看「自己被指派客戶」的發現，含該客戶往年案件歷史），前端不重複判斷。
 *   本頁為唯讀清單，供稽核員查看後到「改善追蹤」新增改善計劃；因客戶要求「不新增資料」，
 *   原 MOCK 版的「新增 NC」「改期限」寫入操作（無後端端點）已移除。
 *
 * Tab 切換靠 URL ?tab=xxx（TabbedPage / @/lib/navigation 已適配 TanStack Router）。
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import dynamic from '@/lib/dynamic';
import { Table, Button, Input, Select } from 'antd';
import type { TableColumnsType } from 'antd';
import { Eye, Search, AlertTriangle, FileText } from 'lucide-react';
import dayjs from 'dayjs';
import TabbedPage from '@/components/common/TabbedPage';
import { ListPageShell, FilterBar, StatusPill, DataField, DataFieldGrid } from '@/components/shell';
import { hideBelow } from '@/components/common/responsive-columns';
import { color, font, text, weight } from '@/styles/tokens';
import { MetaStat, MetaDivider } from '@/components/non-conformities/MetaRow';
import { MonoStrong } from '@/components/non-conformities/cells';
import { renderFilterResetExtra } from '@/components/non-conformities/list-helpers';
import { DetailDrawerShell } from '@/components/non-conformities/DetailDrawerShell';
import {
  useFindings,
  useDebouncedValue,
  type FindingItem,
  type FindingSeverity,
  type FindingStatus,
} from '@/components/non-conformities/use-findings';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/non-conformities/list')({
  component: NCManagementTabPage,
});

/* 動態載入其他 Tab 內容（命名導出，需用 .then 解構為 default） */
const ObservationsContent = dynamic(() =>
  import('@/components/non-conformities/ObservationsTab').then((m) => ({
    default: m.ObservationsTab,
  })),
);
const FollowUpContent = dynamic(() =>
  import('@/components/non-conformities/FollowUpTab').then((m) => ({
    default: m.FollowUpTab,
  })),
);
const ImprovementsContent = dynamic(() =>
  import('@/components/non-conformities/ImprovementsTab').then((m) => ({
    default: m.ImprovementsTab,
  })),
);

/* ════════════════════════════════════════════════
 * 常數：等級 / 狀態的中文標籤與配色
 * ════════════════════════════════════════════════ */

/** 前端三值嚴重程度 → 中文標籤 */
const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  critical: '重大',
  major: '一般',
  minor: '輕微',
};

/** 等級 → StatusPill tone */
const SEVERITY_TONE: Record<FindingSeverity, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  major: 'warning',
  minor: 'info',
};

/** 等級下拉選項 */
const SEVERITY_OPTIONS: Array<{ label: string; value: FindingSeverity }> = [
  { label: '重大', value: 'critical' },
  { label: '一般', value: 'major' },
  { label: '輕微', value: 'minor' },
];

/** 處理狀態 → 中文標籤 */
const STATUS_LABEL: Record<FindingStatus, string> = {
  open: '開立',
  correcting: '改善中',
  closed: '已關閉',
};

/** 狀態 → StatusPill tone */
const STATUS_TONE: Record<FindingStatus, 'info' | 'warning' | 'muted'> = {
  open: 'info',
  correcting: 'warning',
  closed: 'muted',
};

/** 狀態下拉選項 */
const STATUS_OPTIONS: Array<{ label: string; value: FindingStatus }> = [
  { label: '開立', value: 'open' },
  { label: '改善中', value: 'correcting' },
  { label: '已關閉', value: 'closed' },
];

/** 服務器端分頁預設每頁筆數 */
const PAGE_SIZE = 10;

/* ════════════════════════════════════════════════
 * 工具函式
 * ════════════════════════════════════════════════ */

/**
 * 計算逾期天數（正數=逾期，0/負數或已關閉=null）。
 * 已關閉項目視為已處理，回傳 null。
 */
function getOverdueDays(record: FindingItem): number | null {
  if (!record.deadline) return null;
  if (record.status === 'closed') return null;
  const days = dayjs().diff(dayjs(record.deadline), 'day');
  return days > 0 ? days : null;
}

/** ISO → YYYY-MM-DD（null 安全） */
function fmtDate(iso: string | null): string {
  return iso ? dayjs(iso).format('YYYY-MM-DD') : '';
}

/* ════════════════════════════════════════════════
 * NC 列表內容組件（內聯）
 * ════════════════════════════════════════════════ */

function NCReportsContent() {
  /* ---- 分頁（服務器端）---- */
  const [page, setPage] = useState(1);

  /* ---- 篩選（服務器端：keyword 防抖 + severity / status 下推後端，搜尋涵蓋全量）---- */
  const [searchText, setSearchText] = useState('');
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | undefined>();
  const [statusFilter, setStatusFilter] = useState<FindingStatus | undefined>();
  const debouncedSearch = useDebouncedValue(searchText);
  // 搜尋 / 過濾條件變化時回到第 1 頁（CLAUDE.md §5）
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, severityFilter, statusFilter]);

  /* ---- 資料獲取（type=nonconformity → 不符合事項；搜尋 / 過濾由後端處理）---- */
  const { data, isLoading } = useFindings({
    type: 'nonconformity',
    page,
    pageSize: PAGE_SIZE,
    keyword: debouncedSearch || undefined,
    severity: severityFilter,
    status: statusFilter,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  /* ---- 詳情 Drawer ---- */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedNC, setSelectedNC] = useState<FindingItem | null>(null);

  /* ---- 統計（當前頁資料，僅供 meta 行速覽）---- */
  const stats = useMemo(() => {
    const thisMonth = dayjs().format('YYYY-MM');
    return {
      open: items.filter((it) => it.status === 'open').length,
      improving: items.filter((it) => it.status === 'correcting').length,
      closedThisMonth: items.filter(
        (it) => it.status === 'closed' && (it.closedDate ?? '').startsWith(thisMonth),
      ).length,
      overdue: items.filter((it) => (getOverdueDays(it) ?? 0) > 0).length,
    };
  }, [items]);

  /* ---- 事件處理 ---- */

  /** 打開詳情 Drawer */
  const handleViewDetail = (record: FindingItem) => {
    setSelectedNC(record);
    setDetailDrawerOpen(true);
  };

  /* ---- 表格欄位 ---- */
  const columns: TableColumnsType<FindingItem> = [
    {
      title: 'NC 編號',
      dataIndex: 'ncNumber',
      key: 'ncNumber',
      width: 170,
      render: MonoStrong,
    },
    {
      title: '客戶 / 案件',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 240,
      ellipsis: true,
      render: (_: unknown, record: FindingItem) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ color: color.text.primary, fontWeight: weight.medium }}>
            {record.clientName ?? '—'}
          </div>
          <div
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.text.tertiary,
              marginTop: 2,
            }}
          >
            {record.caseCode ?? record.registerCode ?? '—'}
          </div>
        </div>
      ),
    },
    {
      title: '等級',
      dataIndex: 'severity',
      key: 'severity',
      width: 90,
      render: (sev: FindingSeverity) => (
        <StatusPill tone={SEVERITY_TONE[sev]} dot size="sm">
          {SEVERITY_LABEL[sev]}
        </StatusPill>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (st: FindingStatus) => (
        <StatusPill tone={STATUS_TONE[st]} size="sm">
          {STATUS_LABEL[st]}
        </StatusPill>
      ),
    },
    {
      ...hideBelow('lg'),
      title: '改善期限',
      dataIndex: 'deadline',
      key: 'deadline',
      width: 130,
      render: (deadline: string | null) =>
        deadline ? (
          <span style={{ fontFamily: font.mono, color: color.text.secondary }}>
            {fmtDate(deadline)}
          </span>
        ) : (
          <span style={{ color: color.text.muted }}>未設定</span>
        ),
    },
    {
      ...hideBelow('lg'),
      title: '逾期天數',
      key: 'overdue',
      width: 100,
      render: (_: unknown, record: FindingItem) => {
        const days = getOverdueDays(record);
        if (!days) return <span style={{ color: color.text.muted }}>—</span>;
        return (
          <span
            style={{
              fontFamily: font.mono,
              fontWeight: weight.semibold,
              color: color.state.danger,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            +{days}d
          </span>
        );
      },
    },
    {
      ...hideBelow('xl'),
      title: '稽核員',
      dataIndex: 'auditorName',
      key: 'auditorName',
      width: 100,
      render: (name: string | null) => name ?? '—',
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_: unknown, record: FindingItem) => (
        <Button
          type="link"
          size="small"
          icon={<Eye size={14} />}
          onClick={() => handleViewDetail(record)}
        >
          查看
        </Button>
      ),
    },
  ];

  /* ────── page meta 摘要（口徑：當前頁）────── */
  const metaItems = (
    <>
      <MetaStat label="開立中" value={stats.open} tone="info" />
      <MetaDivider />
      <MetaStat label="改善中" value={stats.improving} tone="warning" />
      <MetaDivider />
      <MetaStat label="本月關閉" value={stats.closedThisMonth} tone="success" />
      <MetaDivider />
      <MetaStat
        label="逾期未改善"
        value={stats.overdue}
        tone={stats.overdue > 0 ? 'danger' : 'muted'}
      />
    </>
  );

  return (
    <ListPageShell
      title="不符合事項列表"
      description="現場實地稽核發現的不符合事項（重大 / 一般）。查看後可到「改善追蹤」新增改善計劃。"
      meta={metaItems}
      filters={
        <FilterBar
          search={
            <Input
              placeholder="搜尋 NC 編號 / 客戶 / 案件編號 / 註冊碼..."
              prefix={<Search size={14} color={color.text.muted} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          }
          filters={
            <>
              <Select
                placeholder="等級"
                options={SEVERITY_OPTIONS}
                value={severityFilter}
                onChange={(v) => setSeverityFilter(v)}
                allowClear
                style={{ width: 130 }}
              />
              <Select
                placeholder="狀態"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
                allowClear
                style={{ width: 130 }}
              />
            </>
          }
          extra={renderFilterResetExtra(
            Boolean(searchText || severityFilter || statusFilter),
            () => {
              setSearchText('');
              setSeverityFilter(undefined);
              setStatusFilter(undefined);
            },
          )}
        />
      }
    >
      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: setPage,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 1100 }}
      />

      {/* ===== 詳情 Drawer ===== */}
      <DetailDrawerShell
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        icon={<FileText size={16} color={color.text.muted} />}
        monoTitle={selectedNC?.ncNumber ?? ''}
        statusBadges={
          selectedNC ? (
            <>
              <StatusPill tone={SEVERITY_TONE[selectedNC.severity]} dot>
                {SEVERITY_LABEL[selectedNC.severity]}
              </StatusPill>
              <StatusPill tone={STATUS_TONE[selectedNC.status]}>
                {STATUS_LABEL[selectedNC.status]}
              </StatusPill>
              {(() => {
                const days = getOverdueDays(selectedNC);
                if (!days) return null;
                return (
                  <StatusPill tone="danger" icon={<AlertTriangle size={11} />}>
                    逾期 {days} 天
                  </StatusPill>
                );
              })()}
            </>
          ) : null
        }
      >
        {selectedNC && (
          <DataFieldGrid columns={2}>
            <DataField label="案件編號" value={selectedNC.caseCode ?? '—'} mono />
            <DataField label="稽核員" value={selectedNC.auditorName ?? '—'} />
            <DataField label="客戶" value={selectedNC.clientName ?? '—'} />
            <DataField label="驗證註冊碼" value={selectedNC.registerCode ?? '—'} mono />
            <DataField label="驗證類型" value={selectedNC.verificationType ?? '—'} span={2} />
            <DataField label="相關條款" value={selectedNC.clause ?? '—'} span={2} />
            <DataField label="建立日期" value={fmtDate(selectedNC.createdDate) || '—'} mono />
            <DataField label="改善期限" value={fmtDate(selectedNC.deadline) || '未設定'} mono />
            {selectedNC.closedDate && (
              <DataField label="關閉日期" value={fmtDate(selectedNC.closedDate)} mono span={2} />
            )}
            <DataField label="不符合描述" value={selectedNC.description} span={2} />
            {selectedNC.clientResponse && (
              <DataField label="客戶回覆 / 改善結果" value={selectedNC.clientResponse} span={2} />
            )}
          </DataFieldGrid>
        )}
      </DetailDrawerShell>
    </ListPageShell>
  );
}

/* ════════════════════════════════════════════════
 * 默認導出（Tab 容器）
 * ════════════════════════════════════════════════ */

function NCManagementTabPage() {
  return (
    <TabbedPage
      title="不符合項管理"
      defaultTab="list"
      tabs={[
        { key: 'list', label: 'NC 報告', children: <NCReportsContent /> },
        { key: 'observations', label: '觀察事項', children: <ObservationsContent /> },
        { key: 'follow-up', label: '追蹤稽核', children: <FollowUpContent /> },
        { key: 'improvements', label: '改善追蹤', children: <ImprovementsContent /> },
      ]}
    />
  );
}
